/* eslint-disable dot-notation */
import { defaultUrlMatcher, DefaultUrlSerializer, PRIMARY_OUTLET } from '@angular/router';

import { macAddressRegEx, serialNumberRegEx } from '@shure/shared/angular/ui/validators/common';

interface ClaimUrlParseResult {
	deviceModel?: string; // only in V2 URLs
	mac: string;
	serial: string;
}

/**
 * Parses a claim URL in the V1 (https://claim.shure.com/V1/:serial/:mac) or V2 (https://claim.shure.com/V2/:model/:serial/:mac)
 * format using the same route and param matching facilities as the Angular router.
 * @param urlOrPathname The full URL to parse
 * @returns An object containing the correctly-formatted MAC, serial, and optionally device model from the URL
 */
export function parseClaimUrl(url: string): ClaimUrlParseResult {
	const pathname = new URL(url).pathname.toUpperCase();
	let pathTemplate: string;
	if (pathname.startsWith('/V1')) {
		pathTemplate = 'V1/:serial/:mac';
	} else if (pathname.startsWith('/V2')) {
		pathTemplate = 'V2/:model/:serial/:mac';
	} else {
		throw new Error(`Unexpected claim URL format: ${url}. Expected path to start with /V1 or /V2.`);
	}

	const serializer = new DefaultUrlSerializer();
	const urlTree = serializer.parse(pathname);
	const primaryUrlGroup = urlTree.root.children[PRIMARY_OUTLET];
	if (!primaryUrlGroup) {
		throw new Error(`Unable to parse claim URL: ${url}`);
	}

	const match = defaultUrlMatcher(primaryUrlGroup.segments, primaryUrlGroup, {
		path: pathTemplate
	});
	if (!match) {
		throw new Error(`Unable to parse claim URL: ${url}`);
	}

	// Optional, only exists in V2 URLs
	const deviceModel = match.posParams?.['model']?.path;

	const serial = match.posParams?.['serial']?.path;
	if (!serial) {
		throw new Error(`Serial missing from claim URL: ${url}`);
	}

	const mac = match.posParams?.['mac']?.path;
	if (!mac) {
		throw new Error(`MAC missing from claim URL: ${url}`);
	}

	if (!serialNumberRegEx.test(serial)) {
		throw new Error(`Invalid serial number: ${serial}`);
	}

	const formattedMacAddress = formatMacAddress(mac);
	if (!macAddressRegEx.test(formattedMacAddress)) {
		throw new Error(`Invalid MAC address: ${formattedMacAddress}`);
	}

	return { deviceModel, serial, mac: formattedMacAddress };
}

/**
 * The MAC address in the URL will not contain colons so we need to add them manually.
 * @param mac A raw MAC address string containing no colons
 * @returns A formatted MAC address containing colons
 */
export function formatMacAddress(mac: string): string {
	return mac.replace(/(.{2})(?!$)/g, '$1:');
}
